import React, { useEffect, useRef } from 'react';
import { splitQuotedContent } from '../lib/emailQuoteUtils';
import { sanitizeHtml } from '../lib/sanitizeHtml';

/**
 * Epic 18.3: HTML Sandbox for email body.
 * Renders email body inside a Shadow DOM to isolate internal CSS
 * (e.g. Arial font, blue links) from the CRM's dark mode or layout.
 * Sanitizes: strips <script>, onclick, etc. but preserves layout tags.
 * collapsibleQuoted: when true (thread view), shows quoted content in an expandable section.
 */
const EmailBodySandbox = ({ content, className = '', isDark = false, collapsibleQuoted = false }) => {
    const containerRef = useRef(null);
    const shadowRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;
        let mainHtml = '';
        let quotedHtml = '';
        if (collapsibleQuoted && content) {
            const { newContent, quotedContent } = splitQuotedContent(content);
            mainHtml = sanitizeHtml(newContent);
            quotedHtml = quotedContent ? sanitizeHtml(quotedContent) : '';
        } else {
            mainHtml = sanitizeHtml(content || '');
        }
        if (!shadowRef.current) {
            shadowRef.current = containerRef.current.attachShadow({ mode: 'open' });
        }
        const root = shadowRef.current;
        root.innerHTML = '';
        const wrapper = document.createElement('div');
        wrapper.className = 'email-body-sandbox';
        wrapper.innerHTML = mainHtml || (quotedHtml ? '' : '<p class="text-slate-500">No content</p>');
        const style = document.createElement('style');
        style.textContent = `
            .email-body-sandbox {
                font-family: inherit;
                font-size: 0.875rem;
                line-height: 1.6;
                color: ${isDark ? 'rgb(255,255,255)' : 'rgb(51,65,85)'};
                max-width: 100%;
                overflow-wrap: break-word;
                white-space: pre-wrap;
                word-wrap: break-word;
            }
            .email-body-sandbox p {
                margin: 0 0 0.75em 0;
            }
            .email-body-sandbox p:last-child {
                margin-bottom: 0;
            }
            .email-body-sandbox a {
                color: ${isDark ? 'rgb(147,197,253)' : 'rgb(59,130,246)'};
                text-decoration: underline;
            }
            .email-body-sandbox img {
                max-width: 100%;
                height: auto;
            }
            .email-body-sandbox table {
                max-width: 100%;
            }
            .email-body-sandbox blockquote {
                margin: 0.5em 0;
                padding-left: 1em;
                border-left: 3px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'};
            }
            .email-body-sandbox .quoted-section {
                margin-top: 1em;
                border: 1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
                border-radius: 6px;
                overflow: hidden;
            }
            .email-body-sandbox .quoted-summary {
                padding: 0.5em 0.75em;
                cursor: pointer;
                font-size: 0.8em;
                color: ${isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'};
                background: ${isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)'};
                user-select: none;
            }
            .email-body-sandbox .quoted-summary:hover {
                background: ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'};
            }
            .email-body-sandbox .quoted-body {
                padding: 0.75em 1em;
                font-size: 0.9em;
                opacity: 0.9;
                border-top: 1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
            }
        `;
        if (quotedHtml) {
            const details = document.createElement('details');
            details.className = 'quoted-section';
            details.innerHTML = `
                <summary class="quoted-summary">Show quoted content</summary>
                <div class="quoted-body">${quotedHtml}</div>
            `;
            wrapper.appendChild(details);
        }
        root.appendChild(style);
        root.appendChild(wrapper);
    }, [content, isDark, collapsibleQuoted]);

    return <div ref={containerRef} className={className} />;
};

export default EmailBodySandbox;
